import * as React from 'react';
import { INTERACTIVE_CELL_CLASSNAME } from '@ooh/ui/components/data-table';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import {
  type CellState,
  getCellTdClasses,
  getHatchStyle,
  isMultiSelectEligibleState,
  isShiftSelectableState,
} from '../../../availability';
import {
  buildInventoryAvailabilityGrid,
  getInventoryDeadlineLabelForPeriod,
  getInventoryOwnerConditionalForPeriod,
  getInventoryOwnerHintForPeriod,
  getInventoryOwnerOfferTitleForPeriod,
  type InventoryAvailabilityPeriod,
  type InventoryOccupancyByRowId,
} from '../components/inventory-availability-shared';
import { RichAvailabilityCell } from '../components/availability-rich-cell';
import { AvailabilityLoadMoreAffordance } from '../components/availability-load-more-affordance';
import type { Granularity } from '../components/availability-granularity-select';

/**
 * Shared header renderer for appended rich availability columns. Matches the canonical
 * availability grid header sizing (text-2xs) used by InventoryFullAvailabilityView /
 * AvailabilityGrid so the two views look identical.
 */
function renderPeriodHeader(period: InventoryAvailabilityPeriod) {
  return (
    <span className="block truncate text-2xs font-medium" title={period.label}>
      {period.label}
    </span>
  );
}

/**
 * True when `period` (at `index` in `periods`) starts a new month group — i.e. it's
 * the first period overall, or the previous period's `monthLabel` differs. Used to
 * decide where the Dni-granularity month/year band renders (see renderRichPeriodHeader).
 */
function isMonthGroupStart(periods: InventoryAvailabilityPeriod[], index: number): boolean {
  if (index === 0) return true;
  return periods[index - 1]?.monthLabel !== periods[index]?.monthLabel;
}

/**
 * Header renderer for buildRichAvailabilityColumns. At every granularity other than
 * `day`, `period.label` already carries enough context to be unambiguous (week/half-month
 * labels include the month abbreviation, month labels include the year) — see build-periods.ts.
 *
 * At `day` granularity `period.label` is a bare day number, which repeats every month
 * across the up-to-12-month window and would be ambiguous after horizontal scrolling
 * (spec: "Availability column header carries date context at fine granularity"). Since
 * DataTable renders one independent header cell per column (no colSpan/grouped-header
 * support), the fix is a "band" convention: only the first day column of each month shows
 * the `monthLabel` (e.g. "Maj 2026") above the day number; every other day column in that
 * month omits the repeated label but still carries the full month/year via a `title`
 * tooltip, so no column is left without a resolvable month/year.
 */
function renderRichPeriodHeader(
  period: InventoryAvailabilityPeriod,
  granularity: Granularity,
  groupStart: boolean,
) {
  if (granularity !== 'day') {
    return renderPeriodHeader(period);
  }
  return (
    <div
      className="flex flex-col items-center justify-center leading-tight w-full"
      title={`${period.label} ${period.monthLabel}`}
    >
      {groupStart ? (
        <span className="block truncate max-w-full text-2xs font-semibold text-foreground/70">
          {period.monthLabel}
        </span>
      ) : null}
      <span className="block text-2xs font-medium">{period.label}</span>
    </div>
  );
}

// ─── Rich availability columns ──────────────────────────────────
// Real colors/hatch/icon, an inline owner label, a hover tooltip, and a click popover for
// cell actions via RichAvailabilityCell. Appended to the inventory DataTable when the
// "Dostępność" toggle is on, alongside the regular catalog columns — not a separate view mode.

export interface BuildRichAvailabilityColumnsOptions {
  periods: InventoryAvailabilityPeriod[];
  rowIds: number[];
  occupancyByRowId: InventoryOccupancyByRowId;
  colWidth: number;
  /**
   * Current column granularity — drives the Dni-only month/year header band (see
   * renderRichPeriodHeader). Coarser granularities render `period.label` unchanged.
   */
  granularity: Granularity;
  /**
   * Translated "Wygasa" label prefixing the tooltip's expiry-deadline segment — see
   * `getInventoryDeadlineLabelForPeriod`. Threaded from the caller's own `t()` since
   * this is a plain builder function, not a component.
   */
  expiresLabelPrefix: string;
  openCellKey: string | null;
  onOpenCellKeyChange: (key: string | null) => void;
  /**
   * Rendered inside each cell's click popover (e.g. Rezerwacja/Opcja/Zablokuj actions).
   * Receives the specific row/period so real callers can wire real per-cell actions;
   * mockup callers can ignore the context and return the same static content.
   */
  renderPopoverContent: (ctx: {
    rowId: string;
    period: InventoryAvailabilityPeriod;
    state: CellState;
  }) => React.ReactNode;
  /**
   * Multi-select wiring (task 4.5, `unified-availability-interaction`; widened by task 4.1,
   * `reservation-mixed-range-and-target-expiry`) — additive. When `onCellClick` is
   * provided, a MULTI-SELECT-ELIGIBLE cell (`free`/`option-own`/`reserved-own`/
   * `option-other`/`reserved-other`) gets `RichAvailabilityCell`'s drag mouse handlers
   * wired so a rectangle can pass through or end on it; a lone click routes to select
   * mode (`onSelectClick`) only for the narrower SELECT-eligible subset
   * (`free`/`option-own`/`reserved-own`) — every other multi-select-eligible cell (e.g.
   * `reserved-other`) still opens its own INSPECT popover on a lone click, exactly as
   * before. Cells outside the multi-select-eligible set (`sold`, blocked) keep calling
   * `renderPopoverContent` exactly as before. Omit `onCellClick` to leave every cell on
   * the pre-existing popover-only path (byte-for-byte unchanged default).
   */
  isCellSelected?: (rowId: string, periodId: string) => boolean;
  onCellMouseDown?: (rowId: string, period: InventoryAvailabilityPeriod) => void;
  onCellMouseEnter?: (rowId: string, period: InventoryAvailabilityPeriod) => void;
  onCellMouseUp?: (rowId: string, period: InventoryAvailabilityPeriod) => void;
  onCellClick?: (rowId: string, period: InventoryAvailabilityPeriod, shiftKey: boolean) => void;
}

/**
 * Builds the select-mode props threaded into `RichAvailabilityCell` for a
 * multi-select-eligible cell — extracted so `buildRichAvailabilityColumns`'s `renderCell`
 * closure stays under the complexity budget. Returns all-`undefined`/`false` when
 * `rectangleEligible` is false, matching `RichAvailabilityCell`'s own popover-mode default.
 *
 * `rectangleEligible` (wide — free/option-own/reserved-own/option-other/reserved-other)
 * gates the drag mouse handlers, so a rectangle keeps tracking through/onto this cell.
 * `selectModeEligible` (narrow — free/option-own/reserved-own) additionally gates
 * `onSelectClick`: a lone click on a rectangle-eligible-but-not-select-mode-eligible cell
 * (e.g. `reserved-other`) must fall through to `RichAvailabilityCell`'s own INSPECT
 * popover instead of entering select mode (task 4.1, `reservation-mixed-range-and-target-expiry`).
 */
function buildSelectModeProps(
  rectangleEligible: boolean,
  selectModeEligible: boolean,
  rowId: string,
  period: InventoryAvailabilityPeriod,
  isCellSelected: ((rowId: string, periodId: string) => boolean) | undefined,
  handlers: {
    onCellMouseDown?: (rowId: string, period: InventoryAvailabilityPeriod) => void;
    onCellMouseEnter?: (rowId: string, period: InventoryAvailabilityPeriod) => void;
    onCellMouseUp?: (rowId: string, period: InventoryAvailabilityPeriod) => void;
    onCellClick?: (rowId: string, period: InventoryAvailabilityPeriod, shiftKey: boolean) => void;
  },
) {
  if (!rectangleEligible) {
    return {
      isSelected: false,
      onSelectMouseDown: undefined,
      onSelectMouseEnter: undefined,
      onSelectMouseUp: undefined,
      onSelectClick: undefined,
    };
  }
  return {
    isSelected: isCellSelected?.(rowId, period.id) ?? false,
    onSelectMouseDown: () => handlers.onCellMouseDown?.(rowId, period),
    onSelectMouseEnter: () => handlers.onCellMouseEnter?.(rowId, period),
    onSelectMouseUp: () => handlers.onCellMouseUp?.(rowId, period),
    onSelectClick: selectModeEligible
      ? (shiftKey: boolean) => handlers.onCellClick?.(rowId, period, shiftKey)
      : undefined,
  };
}

/**
 * Generic over the row type `T`, constrained only to `{ id }` — every
 * renderer/classifier below reads solely `item.id`, never any other
 * row-specific field, so any caller's row shape works as long as it carries
 * an id (media-unit rows for the catalog, offer-unit rows for the merged
 * offer table). This lets callers use the real column-def type for their own
 * row instead of an `as unknown as` cast past a row-type mismatch
 * (qual-cast-escape).
 */
export function buildRichAvailabilityColumns<T extends { id: number | string }>({
  periods,
  rowIds,
  occupancyByRowId,
  colWidth,
  granularity,
  expiresLabelPrefix,
  openCellKey,
  onOpenCellKeyChange,
  renderPopoverContent,
  isCellSelected,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  onCellClick,
}: BuildRichAvailabilityColumnsOptions): ColumnDef<T>[] {
  const grid = buildInventoryAvailabilityGrid(rowIds.map(String), periods, occupancyByRowId);
  const resolveCellState = (rowId: string, periodId: string): CellState =>
    grid[rowId]?.[periodId] ?? 'free';
  return periods.map((period, index) => {
    const groupStart = isMonthGroupStart(periods, index);
    return {
      id: `avail-${period.id}`,
      label: period.label,
      align: 'center' as const,
      width: colWidth,
      fixed: true,
      resizable: false,
      // Visually delineates the day-granularity month band boundary (skip the very
      // first column — there's no preceding column to separate it from).
      headerClassName:
        granularity === 'day' && groupStart && index !== 0 ? 'border-l-2 border-border' : undefined,
      renderHeader: () => renderRichPeriodHeader(period, granularity, groupStart),
      cellClassName: (item: T) =>
        `h-10 p-0 ${getCellTdClasses(resolveCellState(String(item.id), period.id), false, undefined, true)}`,
      cellStyle: (item: T) => getHatchStyle(resolveCellState(String(item.id), period.id)),
      renderCell: (item: T) => {
        const rowId = String(item.id);
        const state = resolveCellState(rowId, period.id);
        const ownerHint = getInventoryOwnerHintForPeriod(rowId, period, occupancyByRowId);
        const offerTitle = getInventoryOwnerOfferTitleForPeriod(rowId, period, occupancyByRowId);
        const ownerConditional = getInventoryOwnerConditionalForPeriod(
          rowId,
          period,
          occupancyByRowId,
        );
        const deadlineLabel = getInventoryDeadlineLabelForPeriod(
          rowId,
          period,
          occupancyByRowId,
          expiresLabelPrefix,
        );
        const cellKey = `${rowId}:${period.id}`;
        const rectangleEligible = onCellClick !== undefined && isMultiSelectEligibleState(state);
        const selectModeEligible = onCellClick !== undefined && isShiftSelectableState(state);
        const selectModeProps = buildSelectModeProps(
          rectangleEligible,
          selectModeEligible,
          rowId,
          period,
          isCellSelected,
          { onCellMouseDown, onCellMouseEnter, onCellMouseUp, onCellClick },
        );
        return (
          <RichAvailabilityCell
            state={state}
            ownerHint={ownerHint}
            offerTitle={offerTitle}
            ownerConditional={ownerConditional}
            deadlineLabel={deadlineLabel}
            colWidth={colWidth}
            isOpen={!selectModeEligible && openCellKey === cellKey}
            onOpenChange={(open) => onOpenCellKeyChange(open ? cellKey : null)}
            popoverContent={
              selectModeEligible ? null : renderPopoverContent({ rowId, period, state })
            }
            rowId={rowId}
            periodId={period.id}
            {...selectModeProps}
          />
        );
      },
    };
  });
}

// ─── Trailing "load more periods" column ────────────────────────
// Real appended column (not an overlay) so it sits at the true right edge of the
// availability columns and scrolls into view exactly when the user reaches the end of
// the loaded timeline. Every row's cell repeats the same clickable chevron, reading as
// one continuous vertical bar spanning the table height.

export const LOAD_MORE_COL_WIDTH = 56;

/** Generic over the row type `T` — this column never reads any row field (qual-cast-escape). */
export function buildLoadMoreColumn<T extends { id: number | string }>(
  label: string,
  isLoading: boolean,
  onLoadMore: () => void,
): ColumnDef<T>[] {
  return [
    {
      id: 'avail-load-more',
      label,
      align: 'center' as const,
      width: LOAD_MORE_COL_WIDTH,
      fixed: true,
      resizable: false,
      headerClassName: 'p-0',
      renderHeader: () => (
        <AvailabilityLoadMoreAffordance
          variant="header"
          label={label}
          isLoading={isLoading}
          onLoadMore={onLoadMore}
        />
      ),
      // Elevated (relative z-[1]) above the row-link stretched-link overlay so clicking
      // the "load more" chevron always wins the hit-test over row navigation — see
      // AvailabilityLoadMoreAffordance's own onClick and the overlay z-index scheme
      // documented in @ooh/ui's ROW_LINK_ANCHOR_CLASSNAME / INTERACTIVE_CELL_CLASSNAME.
      cellClassName: `${INTERACTIVE_CELL_CLASSNAME} p-0 h-10 border-l border-border/60`,
      renderCell: () => (
        <AvailabilityLoadMoreAffordance
          variant="cell"
          label={label}
          isLoading={isLoading}
          onLoadMore={onLoadMore}
        />
      ),
    },
  ];
}
